import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, LOCALE_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { finalize, first } from 'rxjs/operators';
import { AbsenceService } from 'src/app/data/absence.service';
import { AbsencesRefreshService } from 'src/app/absences/services/absences-refresh.service';
import { AbsenceLimitsDto } from 'src/app/models/dtos/absence-limits-dto';
import { AbsenceLimitType } from 'src/app/models/enums/absence-limit-type';
import { AbsenceLimitDisplay, mapAbsenceLimitsToDisplay } from './worker-absence-limits-list.mapper';

@Component({
  selector: 'app-worker-absence-limits-list',
  templateUrl: './worker-absence-limits-list.component.html',
  styleUrls: ['./worker-absence-limits-list.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkerAbsenceLimitsListComponent {
  private absenceService = inject(AbsenceService);
  private spinner = inject(NgxSpinnerService);
  private translateService = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private localeId = inject(LOCALE_ID);
  private refreshService = inject(AbsencesRefreshService);

  absenceLimits = signal<AbsenceLimitsDto[]>([]);
  private formattingLocale = signal(this.getFormattingLocale());

  readonly workerAgreementId = input<number | null>(null);

  AbsenceLimitType = AbsenceLimitType;

  absenceTypesMap = computed<Partial<Record<AbsenceLimitType, AbsenceLimitDisplay>>>(() => {
    return mapAbsenceLimitsToDisplay(this.absenceLimits(), this.formattingLocale());
  });

  constructor() {
    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.formattingLocale.set(this.getFormattingLocale());
    });

    effect((onCleanup) => {
      const id = this.workerAgreementId();
      this.refreshService.refreshToken();
      if (id != null) {
        const requestSubscription = this.fetchAbsenceLimits(id);
        onCleanup(() => requestSubscription.unsubscribe());
      } else {
        this.absenceLimits.set([]);
      }
    })
  }

  private fetchAbsenceLimits(id: number): Subscription {
    this.spinner.show();
    return this.absenceService
      .getAbsenceLimitsByAgreementId(id)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((absenceLimits) => this.absenceLimits.set(absenceLimits));
  }

  private getFormattingLocale(): string {
    return this.translateService.getCurrentLang() || this.translateService.getFallbackLang() || this.localeId;
  }
}